import React from 'react';
import { useTranslation } from 'react-i18next';
import { HiChevronUp, HiChevronDown } from 'react-icons/hi';
import {
  HiArrowTopRightOnSquare,
  HiPencilSquare,
  HiTrash,
  HiLink,
} from 'react-icons/hi2';
import {
  IIstiftaLink,
  IIstiftaLinkCategory,
} from '../../common/interfaces/IstiftaLinks.interface';
import { CategoryPill } from './CategoryPill';
import { GenderBadge } from './GenderBadge';
import { EmptyState } from '../Shared/EmptyState/EmptyState';

interface IstiftaLinksTableProps {
  links: IIstiftaLink[];
  categories: IIstiftaLinkCategory[];
  isReordering?: boolean;
  onEdit: (link: IIstiftaLink) => void;
  onDelete: (link: IIstiftaLink) => void;
  onReorder: (ids: string[]) => void;
}

const MAX_HOSTNAME_LEN = 30;

const CATEGORY_PRIORITY: Record<string, number> = {
  tahara: 1,
  salah: 2,
};

function categoryPriority(key: string): number {
  return CATEGORY_PRIORITY[key] ?? Number.MAX_SAFE_INTEGER;
}

function truncateHostname(url: string): string {
  try {
    const host = new URL(url).hostname;
    return host.length > MAX_HOSTNAME_LEN
      ? `${host.slice(0, MAX_HOSTNAME_LEN)}…`
      : host;
  } catch {
    return url;
  }
}

function SkeletonRow() {
  return (
    <tr className="animate-pulse border-b border-primary-50 dark:border-primary-800">
      {Array.from({ length: 4 }).map((_, i) => (
        <td key={i} className="px-4 py-3">
          <div className="h-4 rounded bg-primary-50 dark:bg-indigo-600" />
        </td>
      ))}
    </tr>
  );
}

interface LinkRowProps {
  link: IIstiftaLink;
  index: number;
  total: number;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

function LinkRow({
  link,
  index,
  total,
  onMoveUp,
  onMoveDown,
  onEdit,
  onDelete,
}: LinkRowProps) {
  return (
    <tr className="border-b border-primary-50 bg-white transition-colors hover:bg-primary-25 dark:border-primary-800 dark:bg-indigo-500 dark:hover:bg-indigo-400">
      <td className="w-14 px-4 py-3 align-top">
        <div className="flex items-center gap-1">
          <span>{index + 1}</span>
          <div className="flex flex-col">
            <button
              aria-label="move up"
              disabled={index === 0}
              onClick={onMoveUp}
              className="hover:text-primary-600 disabled:opacity-30"
            >
              <HiChevronUp className="h-3 w-3" />
            </button>
            <button
              aria-label="move down"
              disabled={index === total - 1}
              onClick={onMoveDown}
              className="hover:text-primary-600 disabled:opacity-30"
            >
              <HiChevronDown className="h-3 w-3" />
            </button>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 align-top">
        <a
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          title={link.url}
          className="mb-1 inline-flex w-fit items-center gap-1 text-xs text-primary-600 hover:underline dark:text-primary-300"
        >
          <HiArrowTopRightOnSquare className="h-3 w-3 shrink-0" aria-hidden />
          <span className="max-w-[180px] truncate">
            {truncateHostname(link.url)}
          </span>
        </a>
        <div className="flex flex-col gap-0.5">
          <span
            className="line-clamp-2 text-sm text-gray-900 dark:text-primary-25"
            title={link.description.ar}
          >
            {link.description.ar}
          </span>
          <span
            className="line-clamp-2 text-xs text-gray-500 dark:text-primary-200"
            title={link.description.en}
            lang="en"
            dir="ltr"
          >
            {link.description.en}
          </span>
        </div>
      </td>
      <td className="px-4 py-3 align-top">
        <GenderBadge gender={link.interestGender} />
      </td>
      <td className="px-4 py-3 align-top">
        <div className="flex items-center gap-1">
          <button
            aria-label="edit"
            onClick={onEdit}
            className="rounded-md p-1.5 text-gray-600 transition-colors hover:bg-primary-25 hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:hover:bg-indigo-400 dark:hover:text-primary-200 dark:focus:ring-primary-700"
          >
            <HiPencilSquare className="h-4 w-4" />
          </button>
          <button
            aria-label="delete"
            onClick={onDelete}
            className="rounded-md p-1.5 text-gray-600 transition-colors hover:bg-error-50 hover:text-error-700 focus:outline-none focus:ring-2 focus:ring-error-300 dark:hover:bg-error-900/30 dark:hover:text-error-200 dark:focus:ring-error-700"
          >
            <HiTrash className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

interface CategorySectionProps {
  category: IIstiftaLinkCategory;
  categoryLinks: IIstiftaLink[];
  allLinks: IIstiftaLink[];
  isReordering: boolean;
  onEdit: (link: IIstiftaLink) => void;
  onDelete: (link: IIstiftaLink) => void;
  onReorder: (ids: string[]) => void;
}

function CategorySection({
  category,
  categoryLinks,
  allLinks,
  isReordering,
  onEdit,
  onDelete,
  onReorder,
}: CategorySectionProps) {
  const { t } = useTranslation();

  const swap = (fromIndex: number, toIndex: number) => {
    const reordered = [...categoryLinks];
    [reordered[fromIndex], reordered[toIndex]] = [
      reordered[toIndex],
      reordered[fromIndex],
    ];
    // Rebuild global id list: keep all links in order, replacing this category's
    // block with the newly-reordered sequence.
    const catKey = category.key.toLowerCase();
    let catIdx = 0;
    const globalIds = allLinks.map((l) =>
      l.category.key.toLowerCase() === catKey ? reordered[catIdx++].id : l.id
    );
    onReorder(globalIds);
  };

  const displayName = category.name.ar;

  return (
    <div className="mb-6">
      <div className="mb-2 flex items-center gap-2">
        <CategoryPill categoryKey={category.key} categoryName={displayName} />
        <span className="text-xs text-gray-500 dark:text-primary-200">
          ({categoryLinks.length})
        </span>
      </div>
      <div className="overflow-x-auto rounded-lg bg-white shadow dark:bg-indigo-500">
        <table
          className="w-full text-right text-sm text-gray-900 dark:text-primary-25"
          dir="rtl"
        >
          <thead className="bg-primary-50 text-xs text-gray-700 dark:bg-primary-500 dark:text-white">
            <tr>
              <th className="w-14 px-4 py-3 font-medium">
                {t('istiftaLinks.columns.order')}
              </th>
              <th className="px-4 py-3 font-medium">
                {t('istiftaLinks.columns.description')}
              </th>
              <th className="px-4 py-3 font-medium">
                {t('istiftaLinks.columns.gender')}
              </th>
              <th className="px-4 py-3 font-medium">
                {t('istiftaLinks.columns.actions')}
              </th>
            </tr>
          </thead>
          <tbody>
            {isReordering ? (
              Array.from({ length: 3 }).map((_, i) => <SkeletonRow key={i} />)
            ) : categoryLinks.length === 0 ? (
              <tr>
                <td colSpan={4}>
                  <EmptyState
                    Icon={HiLink}
                    title={t('istiftaLinks.sections.empty')}
                    hint=""
                  />
                </td>
              </tr>
            ) : (
              categoryLinks.map((link, index) => (
                <LinkRow
                  key={link.id}
                  link={link}
                  index={index}
                  total={categoryLinks.length}
                  onMoveUp={() => swap(index, index - 1)}
                  onMoveDown={() => swap(index, index + 1)}
                  onEdit={() => onEdit(link)}
                  onDelete={() => onDelete(link)}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export const IstiftaLinksTable: React.FC<IstiftaLinksTableProps> = ({
  links,
  categories,
  isReordering = false,
  onEdit,
  onDelete,
  onReorder,
}) => {
  const { t } = useTranslation();

  if (categories.length === 0 && links.length === 0) {
    return (
      <div className="overflow-x-auto rounded-lg bg-white shadow dark:bg-indigo-500">
        <EmptyState
          Icon={HiLink}
          title={t('istiftaLinks.empty')}
          hint={t('istiftaLinks.emptyHint')}
        />
      </div>
    );
  }

  // Normalize keys to lowercase — backend sends mixed casing (e.g. "SALAH" vs "salah")
  const linksByCategory = new Map<string, IIstiftaLink[]>();
  for (const link of [...links].sort((a, b) => a.order - b.order)) {
    const key = link.category.key.toLowerCase();
    const bucket = linksByCategory.get(key) ?? [];
    if (!linksByCategory.has(key)) linksByCategory.set(key, bucket);
    bucket.push(link);
  }

  // Build displayed categories from the link data; use categories prop for ordering/name metadata.
  const categoryMetaByKey = new Map(
    categories.map((c) => [c.key.toLowerCase(), c])
  );
  const sortedCategories = Array.from(linksByCategory.keys())
    .map((key) => {
      const meta = categoryMetaByKey.get(key);
      const fallbackName = links.find(
        (l) => l.category.key.toLowerCase() === key
      )?.category.name ?? { ar: key, en: key };
      return {
        key,
        name: meta?.name ?? fallbackName,
        order: meta?.order ?? Number.MAX_SAFE_INTEGER,
      };
    })
    .sort((a, b) => {
      const pa = categoryPriority(a.key);
      const pb = categoryPriority(b.key);
      return pa !== pb ? pa - pb : a.order - b.order;
    });

  return (
    <div>
      {sortedCategories.map((cat) => (
        <CategorySection
          key={cat.key}
          category={cat}
          categoryLinks={linksByCategory.get(cat.key) ?? []}
          allLinks={links}
          isReordering={isReordering}
          onEdit={onEdit}
          onDelete={onDelete}
          onReorder={onReorder}
        />
      ))}
    </div>
  );
};
